import React, { useState, useEffect } from 'react';
import { X, Download, Printer, ShareNetwork } from '@phosphor-icons/react';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { toast } from 'sonner';
import {db} from "../lib/firebase";
import {formatDate, getReadinessColor, getScoreColor} from "../utils/helpers";
import {Button} from "./ui/button";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "./ui/tabs";
import {Progress} from "./ui/progress";
import {Card} from "./ui/card";

const AssessmentDetailModal = ({ assessment, onClose, onUpdate }) => {
    const [loading, setLoading] = useState(false);
    const [detailedAnalysis, setDetailedAnalysis] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        fetchDetailedAnalysis();
        trackAssessmentView();
    }, [assessment.id]);

    const fetchDetailedAnalysis = async () => {
        if (assessment.detailedAnalysisId) {
            try {
                const analysisRef = doc(db, 'detailedAnalyses', assessment.detailedAnalysisId);
                const analysisDoc = await getDoc(analysisRef);
                if (analysisDoc.exists()) {
                    setDetailedAnalysis(analysisDoc.data());
                }
            } catch (error) {
                console.error('Error fetching detailed analysis:', error);
            }
        }
    };

    const trackAssessmentView = async () => {
        try {
            const assessmentRef = doc(db, 'assessments', assessment.id);
            await updateDoc(assessmentRef, {
                view_count: increment(1),
                last_viewed_at: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error tracking view:', error);
        }
    };

    const handleExportPDF = async () => {
        setLoading(true);
        try {
            // Generate PDF report
            const response = await fetch('/api/export-assessment-pdf', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ assessmentId: assessment.id })
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `assessment-${assessment.id}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                toast.success('PDF exported successfully');
            } else {
                throw new Error('Export failed');
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
            toast.error('Failed to export PDF');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
      <html>
        <head>
          <title>Assessment Report - ${assessment.id}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; }
            .header { text-align: center; margin-bottom: 30px; }
            .score { font-size: 48px; font-weight: bold; }
            .outcome { margin-bottom: 20px; padding: 10px; border: 1px solid #ddd; }
            .strength { background: #e6f4ea; padding: 10px; margin: 5px 0; }
            .improvement { background: #fff3e0; padding: 10px; margin: 5px 0; }
            @media print {
              button { display: none; }
            }
          </style>
        </head>
        <body>
          ${document.getElementById('assessment-report-content').innerHTML}
        </body>
      </html>
    `);
        printWindow.document.close();
        printWindow.print();
    };

    const handleShare = async () => {
        const shareData = {
            title: 'MyProfessionalPath Report',
            text: `My MyProfessionalPath score: ${assessment.overall_score}/100 - ${assessment.readiness_level}`,
            url: window.location.href
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
                toast.success('Shared successfully');
            } else {
                await navigator.clipboard.writeText(window.location.href);
                toast.success('Link copied to clipboard');
            }
        } catch (error) {
            console.error('Error sharing:', error);
        }
    };

    const getOutcomeDescription = (outcomeNumber) => {
        const descriptions = {
            1: 'Problem Solving',
            2: 'Application of scientific knowledge',
            3: 'Engineering design',
            4: 'Investigations and experiments',
            5: 'Engineering methods and tools',
            6: 'Professional communication',
            7: 'Sustainability and impact',
            8: 'Engineering professionalism',
            9: 'Independent learning',
            10: 'Engineering judgment',
            11: 'Project management'
        };
        return descriptions[outcomeNumber] || `Outcome ${outcomeNumber}`;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="assessment-detail-modal">
            <div className="bg-white rounded-sm w-full max-w-4xl p-8 relative max-h-[90vh] overflow-y-auto">
                <button
                    onClick={onClose}
                    data-testid="close-assessment-detail-btn"
                    className="absolute right-4 top-4 text-[#666666] hover:text-[#0A0A0A] transition-colors z-10"
                >
                    <X size={24} />
                </button>

                <div id="assessment-report-content">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex justify-between items-start">
                            <div>
                                <h2 className="font-heading text-2xl font-semibold text-[#0A0A0A] mb-3">
                                    Assessment Report
                                </h2>
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className={`font-heading text-3xl font-semibold ${getScoreColor(assessment.overall_score)}`}>
                    {assessment.overall_score}/100
                  </span>
                                    <span className={`text-sm font-medium px-3 py-1 rounded-sm ${getReadinessColor(assessment.readiness_level)}`}>
                    {assessment.readiness_level}
                  </span>
                                </div>
                                <p className="text-sm text-[#666666]">
                                    {formatDate(assessment.created_at)}
                                </p>
                                {assessment.workExpTitle && (
                                    <p className="text-xs text-[#666666] mt-2">
                                        Based on: {assessment.workExpTitle}
                                    </p>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleExportPDF}
                                    disabled={loading}
                                    className="border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5"
                                >
                                    <Download size={16} className="mr-2" />
                                    PDF
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handlePrint}
                                    className="border-[#666666] text-[#666666] hover:bg-gray-50"
                                >
                                    <Printer size={16} className="mr-2" />
                                    Print
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleShare}
                                    className="border-[#009944] text-[#009944] hover:bg-[#009944]/5"
                                >
                                    <ShareNetwork size={16} className="mr-2" />
                                    Share
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
                        <TabsList className="grid w-full grid-cols-3">
                            <TabsTrigger value="overview">Overview</TabsTrigger>
                            <TabsTrigger value="detailed">Detailed Analysis</TabsTrigger>
                            <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
                        </TabsList>

                        <TabsContent value="overview" className="mt-6">
                            {/* Outcome Scores */}
                            <div className="mb-8">
                                <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-4">ECSA Outcome Scores</h3>
                                <div className="space-y-4">
                                    {assessment.outcome_scores?.map((outcome) => (
                                        <div key={outcome.outcome_number} className="border border-[#DCDCDA] rounded-sm p-4 hover:shadow-md transition-shadow">
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="bg-[#002FA7]/10 text-[#002FA7] px-2 py-1 rounded-sm text-xs font-medium">
                              ECSA-{String(outcome.outcome_number).padStart(2, '0')}
                            </span>
                                                        <span className={`font-mono font-semibold ${getScoreColor(outcome.score)}`}>
                              {outcome.score}/100
                            </span>
                                                    </div>
                                                    <p className="text-sm text-[#0A0A0A] font-medium mb-2">
                                                        {getOutcomeDescription(outcome.outcome_number)}
                                                    </p>
                                                    <p className="text-sm text-[#666666] leading-relaxed">
                                                        {outcome.evidence || 'No specific evidence provided'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="mt-3">
                                                <Progress value={outcome.score} className="h-2" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Strengths */}
                            {assessment.strengths && assessment.strengths.length > 0 && (
                                <div className="mb-8">
                                    <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-4">Key Strengths</h3>
                                    <div className="space-y-2">
                                        {assessment.strengths.map((strength, idx) => (
                                            <div key={idx} className="flex items-start gap-3 p-3 bg-[#009944]/10 rounded-sm">
                                                <span className="text-[#009944] font-bold text-lg">✓</span>
                                                <p className="text-sm text-[#0A0A0A] flex-1 leading-relaxed">{strength}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Areas for Improvement */}
                            {assessment.areas_for_improvement && assessment.areas_for_improvement.length > 0 && (
                                <div className="mb-8">
                                    <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-4">Areas for Improvement</h3>
                                    <div className="space-y-2">
                                        {assessment.areas_for_improvement.map((area, idx) => (
                                            <div key={idx} className="flex items-start gap-3 p-3 bg-[#FFB800]/10 rounded-sm">
                                                <span className="text-[#FFB800] font-bold text-lg">•</span>
                                                <p className="text-sm text-[#0A0A0A] flex-1 leading-relaxed">{area}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="detailed" className="mt-6">
                            {detailedAnalysis ? (
                                <div className="space-y-6">
                                    <Card className="p-6">
                                        <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-4">Detailed Analysis</h3>
                                        <div className="prose prose-sm max-w-none">
                                            <p className="text-[#666666] leading-relaxed whitespace-pre-wrap">
                                                {detailedAnalysis.content || 'No detailed analysis available'}
                                            </p>
                                        </div>
                                    </Card>

                                    {detailedAnalysis.metadata && (
                                        <Card className="p-6">
                                            <h4 className="font-heading text-md font-medium text-[#0A0A0A] mb-3">Analysis Metadata</h4>
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <span className="text-[#666666]">Model Used:</span>
                                                    <span className="ml-2 text-[#0A0A0A] font-medium">{detailedAnalysis.metadata.model}</span>
                                                </div>
                                                <div>
                                                    <span className="text-[#666666]">Analysis Time:</span>
                                                    <span className="ml-2 text-[#0A0A0A] font-medium">
                            {formatDate(detailedAnalysis.metadata.analyzed_at)}
                          </span>
                                                </div>
                                                <div>
                                                    <span className="text-[#666666]">Confidence Score:</span>
                                                    <span className={`ml-2 font-medium ${getScoreColor(detailedAnalysis.metadata.confidence * 100)}`}>
                            {(detailedAnalysis.metadata.confidence * 100).toFixed(1)}%
                          </span>
                                                </div>
                                            </div>
                                        </Card>
                                    )}
                                </div>
                            ) : (
                                <Card className="p-12 text-center">
                                    <p className="text-[#666666]">Detailed analysis is being processed. Please check back later.</p>
                                </Card>
                            )}
                        </TabsContent>

                        <TabsContent value="recommendations" className="mt-6">
                            <Card className="p-6">
                                <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-4">Personalized Recommendations</h3>
                                <div className="prose prose-sm max-w-none">
                                    <p className="text-[#666666] leading-relaxed whitespace-pre-wrap">
                                        {assessment.recommendations || 'No specific recommendations available at this time.'}
                                    </p>
                                </div>

                                {/* Action Items */}
                                {assessment.action_items && assessment.action_items.length > 0 && (
                                    <div className="mt-6">
                                        <h4 className="font-heading text-md font-medium text-[#0A0A0A] mb-3">Suggested Action Items</h4>
                                        <div className="space-y-2">
                                            {assessment.action_items.map((item, idx) => (
                                                <div key={idx} className="flex items-start gap-3 p-3 bg-[#F3F3F0] rounded-sm">
                                                    <input type="checkbox" className="mt-1" />
                                                    <p className="text-sm text-[#0A0A0A] flex-1">{item}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>

                <div className="flex gap-4 mt-6">
                    <Button
                        onClick={onClose}
                        data-testid="close-btn"
                        variant="outline"
                        className="flex-1 border-[#DCDCDA] hover:bg-gray-50"
                    >
                        Close
                    </Button>
                    {onUpdate && (
                        <Button
                            onClick={() => onUpdate(assessment.id)}
                            className="flex-1 bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm"
                        >
                            Request Re-assessment
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AssessmentDetailModal;